import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TEXTURE =
  "https://images.pexels.com/photos/12564248/pexels-photo-12564248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      const next = new URLSearchParams(location.search).get("next");
      navigate(next === "pricing" ? "/#pricing" : "/dashboard");
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      <div
        className="hidden md:block relative"
        style={{ backgroundImage: `url('${TEXTURE}')`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="overline mb-3">14 / Brandscaling</div>
          <p className="font-display text-2xl text-white max-w-sm leading-tight">
            "Day 12 — first $4K retainer signed. The frameworks just work."
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="font-display text-xl font-medium mb-12 inline-block">
            14<span className="text-zinc-500">/</span>Brandscaling
          </Link>
          <h1 className="font-display text-4xl tracking-tighter font-medium mb-2">Welcome back</h1>
          <p className="text-zinc-500 mb-10">Sign in to access your dashboard.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs text-zinc-500 mb-2 font-mono uppercase tracking-wider">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@brand.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2 font-mono uppercase tracking-wider">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/20 p-3 rounded" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center mt-6"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in..." : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>

          <p className="text-sm text-zinc-500 mt-8">
            New here?{" "}
            <Link to="/register" className="text-white hover:underline" data-testid="login-to-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
